/**
 * Garde anti-page-blanche au retour de background (mobile).
 *
 * Problème : quand l'app reste en arrière-plan plusieurs minutes (onglet
 * Chrome/Safari ou PWA), l'état JS peut devenir incohérent → page blanche
 * au retour. Un refresh règle toujours le problème.
 *
 * Solution : si l'app a été cachée (visibilitychange → hidden) pendant au
 * moins BACKGROUND_RELOAD_THRESHOLD_MS, on recharge la page au retour.
 * Un simple changement d'onglet ou une courte perte de visibilité (< seuil)
 * ne déclenche rien.
 *
 * - iOS / Safari / PWA et Android / Chrome / PWA : visibilitychange est
 *   supporté partout.
 * - pageshow (persisted) couvre la restauration depuis le bfcache, où
 *   visibilitychange peut ne pas se déclencher de façon fiable.
 * - Aucune boucle possible : après window.location.reload(), les timestamps
 *   repartent à zéro.
 */

// Durée minimale en background avant de considérer un reload (60 s).
const BACKGROUND_RELOAD_THRESHOLD_MS = 60_000;

let hiddenAt: number | null = null;
let initialized = false;

function backgroundDurationExceeded(): boolean {
  if (hiddenAt === null) return false;
  return Date.now() - hiddenAt >= BACKGROUND_RELOAD_THRESHOLD_MS;
}

// Pendant le flux OIDC (/auth/bpm, /auth/callback), une navigation est déjà en
// cours — on évite un reload concurrent (même pattern que versionCheck).
function isOidcAuthFlow(): boolean {
  return ['/auth/bpm', '/auth/callback'].some((p) => window.location.pathname.startsWith(p));
}

function maybeReload(): void {
  const shouldReload = backgroundDurationExceeded();
  hiddenAt = null;
  if (!shouldReload || isOidcAuthFlow()) return;

  // Le focus/visibilitychange mobile arrive parfois avant que le réseau (WiFi/cellulaire)
  // ne soit réellement rétabli — un reload immédiat échoue alors (écran "page inaccessible").
  if (navigator.onLine) {
    window.location.reload();
    return;
  }
  window.addEventListener('online', () => window.location.reload(), { once: true });
}

/**
 * Filet de sécurité au retour de background : certains modals (TradePlacementModal,
 * MaintenanceOverlay) posent `document.body.style.overflow = 'hidden'` pour bloquer
 * le scroll pendant qu'ils sont affichés. Si le tab passe en background pendant
 * qu'un modal est ouvert puis que ce modal se démonte via un re-render déclenché
 * par une source externe (websocket, timer), le cleanup React peut se réordonner
 * et laisser `overflow: hidden` orphelin — le scroll de la page reste bloqué au
 * retour, ce qui matche exactement le symptôme rapporté.
 *
 * On nettoie uniquement si :
 *   - `body.style.overflow === 'hidden'` (posé explicitement via inline style)
 *   - ET aucun `.modal-backdrop` n'est actuellement dans le DOM (aucun modal ouvert)
 * → aucun risque de casser un modal légitimement encore affiché.
 */
function clearOrphanBodyOverflow(): void {
  if (typeof document === 'undefined' || !document.body) return;
  if (document.body.style.overflow !== 'hidden') return;
  if (document.querySelector('.modal-backdrop')) return;
  document.body.style.overflow = '';
  // paddingRight peut avoir été posé en même temps (MaintenanceOverlay) — le libérer aussi.
  if (document.body.style.paddingRight) {
    document.body.style.paddingRight = '';
  }
}

export function initBackgroundReloadGuard(): void {
  if (initialized || typeof document === 'undefined') return;
  initialized = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    if (document.visibilityState === 'visible') {
      clearOrphanBodyOverflow();
      maybeReload();
    }
  });

  // Retour depuis le bfcache (iOS Safari notamment) : la page est "gelée",
  // visibilitychange n'est pas garanti — pageshow persisted le couvre.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      clearOrphanBodyOverflow();
      maybeReload();
    }
  });
}
